import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { useLogger } from "../services/logging";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import TradeFormDialog, { TradeFormValues } from "./TradeFormDialog";
import { CREATE_TRADE, UPDATE_TRADE, DELETE_TRADE } from "../graphql/trades";
import {
  CREATE_POSITION,
  UPDATE_POSITION,
  REMOVE_POSITION,
} from "../graphql/positions";
import TradeMarketContext from "./TradeMarketContext";
import TradeContextDialog from "./TradeContextDialog";

export const GET_TRADES = gql`
  query GetTrades {
    trades {
      id
      symbol
      date
      side
      notes
      walletId
      wallet {
        id
        name
        type
        status
        balance
        currency
      }
      positions {
        id
        type
        quantity
        price
        date
        notes
        status
      }
      events {
        id
        date
        type
        description
        eventType
        status
        impact
        probability
        targetPrice
        stopLoss
      }
      marketContext {
        currentPrice
        priceChange
        priceChangePercent
        volume
        lastUpdated
        hasMarketData
        availableIndicators
        companyInfo {
          name
          sector
          industry
          marketCap
        }
        priceComparison {
          entryPrice
          currentPrice
          priceChange
          priceChangePercent
          pnlImpact {
            unrealizedPnl
            pnlPercent
            totalPositionValue
            totalEntryValue
            isProfitable
          }
        }
        marketConditions {
          volatility
          volume
          rsi
          movingAverage
          support
          resistance
          timestamp
          marketSentiment
        }
        whatIfScenarios {
          scenario
          entryPrice
          exitPrice
          entryTime
          exitTime
          pnl
          pnlPercent
          description
          isOptimal
        }
      }
    }
  }
`;

const CREATE_EVENT = gql`
  mutation CreateEvent($data: CreateEventInput!) {
    createEvent(data: $data) {
      id
    }
  }
`;

const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: Int!, $data: UpdateEventInput!) {
    updateEvent(id: $id, data: $data) {
      id
    }
  }
`;

const DELETE_EVENT = gql`
  mutation DeleteEvent($id: Int!) {
    deleteEvent(id: $id) {
      id
    }
  }
`;

interface TradesTableProps {
  selectedWalletId: string | null;
  onWalletChange?: () => void;
}

export default function TradesTable({
  selectedWalletId,
  onWalletChange,
}: TradesTableProps) {
  const logger = useLogger("TradesTable");
  const { data, loading, error, refetch } = useQuery(GET_TRADES);
  const [createTrade] = useMutation(CREATE_TRADE);
  const [updateTrade] = useMutation(UPDATE_TRADE);
  const [deleteTrade] = useMutation(DELETE_TRADE);
  const [createEvent] = useMutation(CREATE_EVENT);
  const [updateEvent] = useMutation(UPDATE_EVENT);
  const [deleteEvent] = useMutation(DELETE_EVENT);
  const [createPosition] = useMutation(CREATE_POSITION);
  const [updatePosition] = useMutation(UPDATE_POSITION);
  const [deletePosition] = useMutation(REMOVE_POSITION);

  const handleFetchTrade = async (tradeId: number) => {
    try {
      const result = await refetch();
      const trade = result.data?.trades?.find((t: any) => t.id === tradeId);
      return trade;
    } catch (error) {
      logger.error("Error fetching trade", { method: "fetchTrade" }, error);
      return null;
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState<any | null>(null);
  const [contextTradeId, setContextTradeId] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingTrade(null);
    setDialogOpen(true);
  };

  const handleEdit = (trade: any) => {
    setEditingTrade(trade);
    setDialogOpen(true);
  };

  const handleDeleteClick = (trade: any) => {
    setTradeToDelete(trade);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tradeToDelete) return;
    try {
      await deleteTrade({ variables: { id: tradeToDelete.id } });
      if (onWalletChange) await onWalletChange(); // Await wallet refetch
      await refetch(); // Await trades refetch
      setDeleteDialogOpen(false);
      setTradeToDelete(null);
    } catch (error) {
      logger.error(
        "Error deleting trade",
        { method: "handleDeleteTrade" },
        error,
      );
      alert("Failed to delete trade. Please try again.");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTradeToDelete(null);
  };

  const handleShowTradeContext = (tradeId: number) => {
    setContextTradeId(tradeId);
  };

  const handleCloseTradeContext = () => {
    setContextTradeId(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = async (values: TradeFormValues) => {
    const tradeInput = {
      symbol: values.symbol,
      date: values.date,
      side: values.side,
      notes: values.notes,
      walletId: selectedWalletId || values.walletId, // Use selected wallet or fallback to form value
    };
    if (editingTrade) {
      const result = await updateTrade({
        variables: { id: editingTrade.id, updateTradeInput: tradeInput },
      });
      if (result.data?.updateTrade) {
        if (onWalletChange) onWalletChange();
        return result.data.updateTrade;
      } else {
        logger.error(
          "Trade update failed",
          { method: "handleSave" },
          { result },
        );
        setDialogOpen(false);
        refetch();
      }
    } else {
      const result = await createTrade({
        variables: { createTradeInput: tradeInput },
      });
      if (result.data?.createTrade) {
        if (onWalletChange) onWalletChange();
        return result.data.createTrade;
      } else {
        logger.error(
          "Trade creation failed",
          { method: "handleSave" },
          { result },
        );
        setDialogOpen(false);
        refetch();
      }
    }
  };

  const columns: GridColDef[] = [
    { field: "symbol", headerName: "Symbol", flex: 1 },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: (params: any) => {
        const positions = params.row?.positions || [];
        let dateText = "No date";
        if (positions.length > 0 && positions[0].date) {
          try {
            dateText = new Date(positions[0].date).toLocaleDateString();
          } catch (error) {
            logger.error(
              "Position date parsing error",
              { method: "render" },
              { error, date: positions[0].date },
            );
            dateText = "Invalid date";
          }
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2">{dateText}</Typography>
          </Box>
        );
      },
    },
    { field: "side", headerName: "Side", flex: 1 },

    {
      field: "positions",
      headerName: "Positions",
      flex: 1,
      renderCell: (params: any) => {
        const positions = params.value || [];
        const totalQuantity = positions.reduce((sum: number, pos: any) => {
          return sum + (pos.quantity || 0);
        }, 0);
        const avgPrice =
          positions.length > 0
            ? positions.reduce(
                (sum: number, pos: any) =>
                  sum + (pos.quantity || 0) * (pos.price || 0),
                0,
              ) / totalQuantity
            : 0;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2">
              {positions.length} pos • {totalQuantity} @ ${avgPrice.toFixed(2)}
            </Typography>
          </Box>
        );
      },
    },
    { field: "notes", headerName: "Notes", flex: 2 },
    {
      field: "marketContext",
      headerName: "Market Data",
      flex: 2,
      sortable: false,
      renderCell: (params: any) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <TradeMarketContext trade={params.row} compact={true} />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleShowTradeContext(params.row.id);
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return <Typography color="error">Error loading trades.</Typography>;

  if (data?.trades) {
    // Debug logging for trade data (commented out for performance)
    // data.trades.forEach((trade: any, index: number) => {
    //   logger.debug(`Trade ${index}`, { method: 'render' }, {
    //     id: trade.id,
    //     symbol: trade.symbol,
    //     positions: trade.positions,
    //     positionsLength: trade.positions?.length || 0
    //   });
    //   if (trade.positions && trade.positions.length > 0) {
    //     logger.debug(`Trade ${index} positions details`, { method: 'render' }, { positions: trade.positions });
    //     trade.positions.forEach((pos: any, posIndex: number) => {
    //       logger.debug(`Trade ${index} Position ${posIndex}`, { method: 'render' }, {
    //         id: pos.id,
    //         type: pos.type,
    //         quantity: pos.quantity,
    //         price: pos.price,
    //         date: pos.date,
    //         notes: pos.notes,
    //         status: pos.status
    //       });
    //     });
    //   }
    // });
  }

  // Filter trades by selected wallet
  const filteredTrades = selectedWalletId
    ? (data?.trades || []).filter(
        (trade: any) => trade.walletId === selectedWalletId,
      )
    : data?.trades || [];

  if (!selectedWalletId) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Please select a wallet to view trades
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="trades-table" sx={{ height: 500, width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          Trades for{" "}
          {data?.trades?.find((t: any) => t.walletId === selectedWalletId)
            ?.wallet?.name || "Selected Wallet"}
        </Typography>
        <Button variant="contained" onClick={handleAdd}>
          Add Trade
        </Button>
      </Stack>
      <DataGrid
        rows={filteredTrades.map((trade: any) => ({
          ...trade,
          positions: trade.positions || [],
        }))}
        columns={columns}
        getRowId={(row: any) => row.id}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10]}
        autoHeight
        onRowClick={(params) => handleEdit(params.row)}
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          },
        }}
      />
      <TradeFormDialog
        open={dialogOpen}
        initialValues={
          editingTrade
            ? {
                ...editingTrade,
                date: new Date(editingTrade.date),
                walletId: editingTrade.walletId || "",
                events: editingTrade.events || [],
                positions: editingTrade.positions || [],
              }
            : undefined
        }
        onClose={handleDialogClose}
        onSave={async (values) => {
          const result = await handleDialogSave(values);
          // Note: Do NOT refetch here - positions are created AFTER onSave returns
          // Refetch happens in TradeFormDialog.handleSave after all positions are created
          return result;
        }}
        onEventCreate={createEvent}
        onEventUpdate={updateEvent}
        onEventDelete={deleteEvent}
        onPositionCreate={createPosition}
        onPositionUpdate={updatePosition}
        onPositionDelete={deletePosition}
        onRefresh={refetch}
        onFetchTrade={handleFetchTrade}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Trade</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" component="div">
            <Typography component="p" variant="body1" color="text.secondary">
              Are you sure you want to <b>permanently delete</b> trade "
              {tradeToDelete?.symbol}"?
            </Typography>
            <Typography component="p" variant="body1" color="text.secondary">
              <b>This action cannot be undone.</b>
            </Typography>
            <Typography component="p" variant="body1" color="text.secondary">
              <b>Deleting this trade will also remove:</b>
            </Typography>
            <ul>
              <li>
                All associated positions (and their effects on wallet balance)
              </li>
              <li>All associated events</li>
              <li>All wallet activity and balance history for this trade</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <TradeContextDialog
        tradeId={contextTradeId}
        open={contextTradeId !== null}
        onClose={handleCloseTradeContext}
      />
    </Box>
  );
}
